/**
 * @file Shared client-side runtime for www's islands.
 * @module @labcraft/www/ui/islands/runtime
 *
 * ADR-026 removed the SPA (router, TanStack cache, ClientProvider);
 * the islands are the only client-side React on the site. This module
 * is their composition layer: one typed `Www` client over one
 * `HttpClientTransport`, plus promise-cached reads of the two
 * whole-page facts (`/api/account/me`, `/api/config`) so islands on
 * the same page share a single fetch instead of stampeding the daemon.
 *
 * The transport is stateless and usable without driving the lifecycle
 * (`request` works pre-`start()`), so no lifecycle orchestration
 * happens here — islands call methods directly.
 */

import { HttpClientTransport } from '@labcraft/core/frontend'
import { Www } from '../../client/index.ts'
import type { WwwMeResponse } from '../../client/account.ts'
import type { WwwConfigResponse } from '../../shared/routes.ts'

/**
 * Production lab origin — the fallback for lab-bound navigation while
 * `/api/config` is in flight or unreachable. Correct in prod, briefly
 * stale in the dev harness until the fetch lands.
 */
export const DEFAULT_LAB_ORIGIN = 'https://lab.labcraft.dev'

/**
 * Island-side service locator for the typed client + the two shared
 * page-level reads. Static-only; all state is module-lifetime (one
 * page load).
 */
export class IslandRuntime {
  /** Lazily-constructed shared client (one per page load). */
  private static client: Www | null = null

  /** Promise-cached `/api/account/me` read. */
  private static mePromise: Promise<WwwMeResponse> | null = null

  /** Promise-cached `/api/config` read. */
  private static configPromise: Promise<WwwConfigResponse> | null = null

  /**
   * The shared typed `Www` client. Constructed on first use with a
   * transport pointed at the page's own origin (Caddy in prod and the
   * Vite proxy in dev both forward `/api/*` to the daemon).
   *
   * @returns The shared client instance.
   */
  static www(): Www {
    if (!IslandRuntime.client) {
      const http = new HttpClientTransport({
        baseUrl: window.location.origin,
      })
      IslandRuntime.client = new Www({ id: 'www' }, { http })
    }
    return IslandRuntime.client
  }

  /**
   * The current learner payload, shared across islands. Anonymous is
   * a payload value (`{ learner: null }`), not an error; transport
   * failures also resolve anonymous so the chrome degrades to the
   * signed-out state rather than breaking.
   *
   * @returns The `/api/account/me` response (cached per page load).
   */
  static me(): Promise<WwwMeResponse> {
    IslandRuntime.mePromise ??= IslandRuntime.www()
      .account.me()
      .catch(() => ({ learner: null }))
    return IslandRuntime.mePromise
  }

  /**
   * The runtime config (waitlist mode + lab origin), shared across
   * islands. Falls back to the production-shaped defaults on failure
   * so a config blip never breaks an island — the same fallbacks the
   * old hooks rendered while in flight.
   *
   * @returns The `/api/config` response (cached per page load).
   */
  static config(): Promise<WwwConfigResponse> {
    IslandRuntime.configPromise ??= IslandRuntime.www()
      .config()
      .catch(() => ({
        waitlistEnabled: false,
        labOrigin: DEFAULT_LAB_ORIGIN,
      }))
    return IslandRuntime.configPromise
  }
}
